import React, {useRef, useState, useEffect} from 'react';

import {cx} from '../util';

// Function to find the effective background color by traversing up the DOM
const findBackgroundColor = (element) => {
  let current = element;
  while (current && current !== document.body) {
    const style = window.getComputedStyle(current);
    const {backgroundColor} = style;

    // Check if we found a non-transparent background
    if (backgroundColor && backgroundColor !== 'rgba(0, 0, 0, 0)' && backgroundColor !== 'transparent') {
      return backgroundColor;
    }
    current = current.parentElement;
  }

  // Fallback: check body and html
  const bodyStyle = window.getComputedStyle(document.body);
  if (bodyStyle.backgroundColor && bodyStyle.backgroundColor !== 'rgba(0, 0, 0, 0)' && bodyStyle.backgroundColor !== 'transparent') {
    return bodyStyle.backgroundColor;
  }

  const htmlStyle = window.getComputedStyle(document.documentElement);
  if (htmlStyle.backgroundColor && htmlStyle.backgroundColor !== 'rgba(0, 0, 0, 0)' && htmlStyle.backgroundColor !== 'transparent') {
    return htmlStyle.backgroundColor;
  }

  return 'white';
};

export const FadeOut = ({
  top, right, bottom, left, children, className, scrollRef,
}) => {
  const containerRef = useRef(null);

  // Allow a parent to drive scrolling (e.g. arrow buttons) by forwarding the
  // inner scroll element. We keep our own containerRef for overflow detection.
  const setRefs = (node) => {
    containerRef.current = node;
    if (scrollRef) {
      // eslint-disable-next-line no-param-reassign
      scrollRef.current = node;
    }
  };
  const [detectedBgColor, setDetectedBgColor] = useState('white');
  const [isOverflowing, setIsOverflowing] = useState({
    top: false,
    right: false,
    bottom: false,
    left: false,
  });

  // Detect background color when component mounts or when dependencies change
  useEffect(() => {
    if (containerRef.current) {
      const bgColor = findBackgroundColor(containerRef.current.parentElement);
      setDetectedBgColor(bgColor);
    }
  }, []);

  // Set up checkOverflow listener. Check on scroll and on resize
  useEffect(() => {
    const checkOverflow = () => {
      if (!containerRef.current) return;

      const element = containerRef.current;
      const {
        scrollTop,
        scrollLeft,
        scrollWidth,
        scrollHeight,
        clientWidth,
        clientHeight,
      } = element;

      setIsOverflowing({
        top: scrollTop > 0,
        right: scrollLeft + clientWidth < scrollWidth - 1,
        bottom: scrollTop + clientHeight < scrollHeight - 1,
        left: scrollLeft > 0,
      });
    };

    checkOverflow();

    const element = containerRef.current;
    if (element) {
      element.addEventListener('scroll', checkOverflow);

      // Defer to the next frame so the ResizeObserver can finish delivering
      // its current batch of notifications before our state update triggers a
      // layout change. Without this, headless Chrome and some Chrome versions
      // throw "ResizeObserver loop completed with undelivered notifications".
      let rafId = null;
      const resizeObserver = new ResizeObserver(() => {
        if (rafId != null) return;
        rafId = window.requestAnimationFrame(() => {
          rafId = null;
          checkOverflow();
        });
      });
      resizeObserver.observe(element);

      return () => {
        element.removeEventListener('scroll', checkOverflow);
        resizeObserver.disconnect();
        if (rafId != null) window.cancelAnimationFrame(rafId);
      };
    }

    return undefined;
  }, [children]);

  return (
    <div className={cx('relative w-full h-full', className)}>
      <div ref={setRefs} className="h-full overflow-auto scrollbar-hidden p-1">
        {children}
      </div>
      {top && isOverflowing.top && (
        <div
          className="absolute pointer-events-none h-4 top-0 left-0 w-full z-10"
          style={{
            background: `linear-gradient(to bottom, ${detectedBgColor}, transparent)`,
          }}
        />
      )}
      {right && isOverflowing.right && (
        <div
          className="absolute pointer-events-none w-4 top-0 right-0 h-full z-10"
          style={{
            background: `linear-gradient(to left, ${detectedBgColor}, transparent)`,
          }}
        />
      )}
      {bottom && isOverflowing.bottom && (
        <div
          className="absolute pointer-events-none h-4 bottom-0 left-0 w-full z-10"
          style={{
            background: `linear-gradient(to top, ${detectedBgColor}, transparent)`,
          }}
        />
      )}
      {left && isOverflowing.left && (
        <div
          className="absolute pointer-events-none w-4 top-0 left-0 h-full z-10"
          style={{
            background: `linear-gradient(to right, ${detectedBgColor}, transparent)`,
          }}
        />
      )}
    </div>
  );
};

/**
 * A general purpose container for children that you want to side scroll,
 * fading to white on desktop and falling off the edge on mobile.
 */
export const MobileAwareFadeOut = ({children, className}) => (
  <>
    <div className={cx('hidden md:block', className)}>
      <FadeOut right left>
        <div className="flex space-x-2">
          {children}
        </div>
      </FadeOut>
    </div>
    <div className={cx('block md:hidden', className)}>
      <div className="overflow-hidden -mx-4 -my-1">
        <div className="flex overflow-x-scroll scrollbar-hidden px-4 py-1 space-x-2">
          {children}
        </div>
      </div>
    </div>
  </>
);
